import { useState, useEffect } from "react";
import { Mail, Lock, Eye, EyeOff, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { forgotPassword } from "../../api/services/authService";
import { getLocalizedErrorMessage } from "../../utils/errorMessages";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import AuthShell from "../../components/Auth/AuthShell";
import ThemeToggle from "../../components/ThemeToggle";

export default function UniversityLogin() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { login, isLoading, user, isInitialized } = useAuth();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  // Set document direction on mount based on current language
  useEffect(() => {
    document.documentElement.dir = i18n.language === "ar" ? "rtl" : "ltr";
  }, [i18n.language]);

  // Redirect to home if already authenticated
  useEffect(() => {
    if (isInitialized && user) {
      navigate("/home");
    }
  }, [isInitialized, user, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await login(
      formData.username,
      formData.password,
      "university_user",
    );
    if (result.ok) {
      navigate("/home");
    } else {
      setError(
        getLocalizedErrorMessage(
          result.errorMessage,
          t,
          t("auth.login.invalid_credentials"),
        ),
      );
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setForgotPasswordMessage("");
    setForgotPasswordLoading(true);

    try {
      const response = await forgotPassword(forgotEmail);

      const localizedMessage = getLocalizedErrorMessage(
        response.message,
        t,
        t("auth.forgot_password.success_message"),
      );
      setForgotPasswordMessage(localizedMessage);

      setTimeout(() => {
        setShowForgotPassword(false);
        setForgotPasswordMessage("");
        setForgotEmail("");
        setForgotPasswordLoading(false);
      }, 3000);
    } catch (error) {
      const localizedError = getLocalizedErrorMessage(
        error.message,
        t,
        t("auth.forgot_password.error_message"),
      );
      setForgotPasswordMessage(localizedError);
      setForgotPasswordLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotEmail("");
    setForgotPasswordMessage("");
  };

  useEffect(() => {
    if (!showForgotPassword) return;
    const emailCandidate = formData.username?.trim();
    if (emailCandidate && emailCandidate.includes("@") && !forgotEmail) {
      setForgotEmail(emailCandidate);
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeForgotPassword();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [formData.username, forgotEmail, showForgotPassword]);

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-(--color-bg)">
        <div className="text-(--color-primary) text-lg">
          {t("auth.login.loading")}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <div className="fixed bottom-6 right-6 z-40">
        <ThemeToggle />
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div
          className="fixed inset-0 bg-black/65 backdrop-blur-[6px] flex items-center justify-center z-50"
          onClick={closeForgotPassword}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="forgot-password-title"
            className="bg-(--color-bg-card) border border-(--color-border) shadow-[0_20px_60px_rgba(0,0,0,0.18)] rounded-sm p-8 w-full max-w-md mx-4 relative"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              onClick={closeForgotPassword}
              className="absolute top-4 right-4 text-(--color-text-muted) hover:text-(--color-primary) transition-colors"
            >
              <X size={20} />
            </button>

            <h2
              id="forgot-password-title"
              className="qv-serif text-[26px] sm:text-[28px] font-medium tracking-tight text-(--color-text-primary) text-center mb-2"
            >
              {t("auth.forgot_password.title")}
            </h2>
            <p className="text-[13px] text-(--color-text-secondary) text-center mb-6">
              {t("auth.forgot_password.description")}
            </p>

            <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
              <Input
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder={t("auth.forgot_password.email_placeholder")}
                icon={Mail}
                required
                autoFocus
              />

              {forgotPasswordMessage && (
                <div className="p-3 bg-(--color-primary-50) border border-(--color-primary) rounded-lg text-(--color-text-primary) text-sm text-center [text-shadow:0_0_8px_var(--color-shadow-primary)]">
                  {forgotPasswordMessage}
                </div>
              )}

              <Button
                type="submit"
                disabled={!forgotEmail || forgotPasswordLoading}
              >
                {forgotPasswordLoading
                  ? t("auth.forgot_password.sending")
                  : t("auth.forgot_password.send_button")}
              </Button>

              <button
                type="button"
                onClick={closeForgotPassword}
                className="w-full py-3 text-(--color-text-muted) hover:text-(--color-primary) transition-colors text-sm"
              >
                {t("auth.forgot_password.cancel")}
              </button>
            </form>
          </div>
        </div>
      )}

      <AuthShell
        tagline={t("auth.university_login.tagline")}
        title={
          <>
            {t("auth.university_login.hero_line1_prefix")}{" "}
            <span className="text-(--color-primary)">
              {t("auth.university_login.hero_line1_highlight")}
            </span>
            <br />
            {t("auth.university_login.hero_line2_prefix")}{" "}
            <span className="text-(--color-primary)">
              {t("auth.university_login.hero_line2_highlight")}
            </span>
          </>
        }
        subtitle={t("auth.login.university_subtitle")}
        formEyebrow={t("auth.university_login.form_eyebrow")}
        formTitle={t("auth.university_login.form_title")}
        formSubtitle={t("auth.login.university_subtitle")}
      >
        <form onSubmit={handleSubmit} className="space-y-3">
          <Input
            type="text"
            name="username"
            value={formData.username}
            onChange={handleChange}
            placeholder={t("auth.login.username_placeholder")}
            icon={Mail}
            required
          />

          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={t("auth.login.password_placeholder")}
              icon={Lock}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className={`absolute ${i18n.language === "ar" ? "left-3" : "right-3"} top-1/2 -translate-y-1/2 text-(--color-text-muted) hover:text-(--color-primary) transition-colors`}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <div className="text-right">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-(--color-primary) hover:underline"
            >
              {t("auth.login.forgot_password")}
            </button>
          </div>

          {error && <div className="text-red-500 text-sm">{error}</div>}

          <Button type="submit" disabled={isLoading}>
            {isLoading
              ? t("auth.login.logging_in")
              : t("auth.login.login_button")}
          </Button>
        </form>

        <div className="mt-5 text-center text-sm text-(--color-text-muted)">
          <span>{t("auth.login.signup_text")} </span>
          <button
            onClick={() => navigate("/register")}
            className="accent-link ml-1 font-medium"
          >
            {t("auth.login.signup_link")}
          </button>
        </div>
      </AuthShell>
    </div>
  );
}
